/**
 * NuGuard AI - Shared Utilities Library
 * 
 * Common utility functions used across the application.
 * Includes class name merging, formatting, and helper functions.
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// =============================================================================
// Class Name Utilities
// =============================================================================

/**
 * Merge Tailwind CSS classes with proper conflict resolution.
 * Uses clsx for conditional classes and tailwind-merge to resolve conflicts.
 * 
 * @example
 * cn('px-4 py-2', isActive && 'bg-primary-500', className)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// =============================================================================
// Date & Time Formatting (All times displayed in user's local timezone)
// =============================================================================

/**
 * Get the user's timezone abbreviation (e.g., "PST", "EST", "IST")
 */
export function getUserTimezone(): string {
  return new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
}

/**
 * Normalize a date value so that bare datetime strings (no timezone suffix)
 * are treated as UTC rather than local time by the browser's Date parser.
 * Numbers and Date objects are passed through unchanged.
 */
function normalizeDate(date: string | number | Date): string | number | Date {
  if (typeof date === 'string' && date.length > 0 && !/Z$|[+-]\d{2}:\d{2}$/.test(date)) {
    return date + 'Z';
  }
  return date;
}

/**
 * Format a date string or timestamp to a localized date string.
 * Shows date in user's local timezone.
 * 
 * @param date - Date to format
 * @param options - Optional formatting options
 * @returns Formatted date string (e.g., "12/31/2024")
 */
export function formatDate(date: string | number | Date, options?: Intl.DateTimeFormatOptions): string {
  return new Date(normalizeDate(date)).toLocaleDateString(undefined, options);
}

/**
 * Format a date string or timestamp to a localized date-time string.
 * Shows date and time in user's local timezone with optional timezone indicator.
 * 
 * @param date - Date to format
 * @param showTimezone - Whether to show timezone abbreviation (default: false)
 * @returns Formatted date-time string (e.g., "12/31/2024, 3:45 PM" or "12/31/2024, 3:45 PM PST")
 */
export function formatDateTime(date: string | number | Date, showTimezone: boolean = false): string {
  const d = new Date(normalizeDate(date));
  const formatted = d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return showTimezone ? `${formatted} ${getUserTimezone()}` : formatted;
}

/**
 * Format a date string or timestamp to a localized time string.
 * Shows time in user's local timezone.
 * 
 * @param date - Date to format
 * @param showTimezone - Whether to show timezone abbreviation (default: false)
 * @returns Formatted time string (e.g., "3:45 PM" or "3:45 PM PST")
 */
export function formatTime(date: string | number | Date, showTimezone: boolean = false): string {
  const d = new Date(normalizeDate(date));
  const formatted = d.toLocaleTimeString(undefined, { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true,
  });
  return showTimezone ? `${formatted} ${getUserTimezone()}` : formatted;
}

/**
 * Format a date string with custom options.
 * Provides full control over date/time formatting in user's local timezone.
 * 
 * @param date - Date to format
 * @param options - Intl.DateTimeFormatOptions for custom formatting
 * @returns Formatted date string
 */
export function formatDateCustom(date: string | number | Date, options: Intl.DateTimeFormatOptions): string {
  return new Date(normalizeDate(date)).toLocaleString(undefined, options);
}

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 * Shows relative time based on user's local timezone.
 * 
 * @param date - Date to compare against current time
 * @returns Human-readable relative time string
 */
export function formatRelativeTime(date: string | number | Date): string {
  const now = new Date();
  const then = new Date(normalizeDate(date));
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
}

/**
 * Format a date for display with month name.
 * Shows in user's local timezone.
 * 
 * @param date - Date to format
 * @returns Formatted date (e.g., "Jan 15, 2024")
 */
export function formatDateShort(date: string | number | Date): string {
  return new Date(normalizeDate(date)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date-time for display with month name.
 * Shows in user's local timezone.
 * 
 * @param date - Date to format
 * @param showTimezone - Whether to show timezone abbreviation (default: false)
 * @returns Formatted date-time (e.g., "Jan 15, 2024, 3:45 PM PST")
 */
export function formatDateTimeShort(date: string | number | Date, showTimezone: boolean = false): string {
  const d = new Date(normalizeDate(date));
  const formatted = d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return showTimezone ? `${formatted} ${getUserTimezone()}` : formatted;
}

// =============================================================================
// Number Formatting
// =============================================================================

/**
 * Format a number as a percentage string.
 */
export function formatPercent(value: number, decimals: number = 0): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a number with commas for thousands.
 */
export function formatNumber(value: number): string {
  return value.toLocaleString();
}

/**
 * Build Risk Analysis route with optional application deep-link query parameter.
 */
export function buildRiskAnalysisUrl(appId?: string | null): string {
  const trimmedAppId = appId?.trim();
  if (!trimmedAppId) return '/risks';

  const params = new URLSearchParams({ app: trimmedAppId });
  return `/risks?${params.toString()}`;
}

// =============================================================================
// String Utilities
// =============================================================================

/**
 * Truncate a string to a maximum length with ellipsis.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
}

/**
 * Get initials from a name (e.g., "John Doe" -> "JD")
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

/**
 * Capitalize the first letter of a string.
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// =============================================================================
// Color Utilities
// =============================================================================

/**
 * Get severity color classes based on severity level.
 */
export function getSeverityColor(severity: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return {
        bg: 'bg-red-500/10',
        text: 'text-red-600 dark:text-red-400',
        border: 'border-red-500/20',
      };
    case 'HIGH':
      return {
        bg: 'bg-orange-500/10',
        text: 'text-orange-600 dark:text-orange-400',
        border: 'border-orange-500/20',
      };
    case 'MEDIUM':
      return {
        bg: 'bg-yellow-500/10',
        text: 'text-yellow-500 dark:text-yellow-400',
        border: 'border-yellow-500/20',
      };
    case 'LOW':
      return {
        bg: 'bg-green-500/10',
        text: 'text-green-600 dark:text-green-400',
        border: 'border-green-500/20',
      };
    case 'INFO':
      return {
        bg: 'bg-gray-500/10',
        text: 'text-gray-600 dark:text-gray-400',
        border: 'border-gray-500/20',
      };
    default:
      return {
        bg: 'bg-stone-200 dark:bg-nuguard-card',
        text: 'text-stone-600 dark:text-slate-400',
        border: 'border-stone-200 dark:border-nuguard-edge',
      };
  }
}

/**
 * Get coverage color classes based on percentage.
 */
export function getCoverageColor(coverage: number): {
  bg: string;
  text: string;
  border: string;
} {
  if (coverage < 50) {
    return {
      bg: 'bg-red-500/10',
      text: 'text-red-600 dark:text-red-500',
      border: 'border-red-500/20',
    };
  }
  if (coverage < 80) {
    return {
      bg: 'bg-yellow-500/10',
      text: 'text-yellow-600 dark:text-yellow-500',
      border: 'border-yellow-500/20',
    };
  }
  return {
    bg: 'bg-green-500/10',
    text: 'text-green-600 dark:text-green-500',
    border: 'border-green-500/20',
  };
}

/**
 * Get status color classes based on status string.
 */
export function getStatusColor(status: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (status.toLowerCase()) {
    case 'active':
    case 'compliant':
    case 'healthy':
    case 'passed':
      return {
        bg: 'bg-green-500/10',
        text: 'text-green-600 dark:text-green-500',
        border: 'border-green-500/20',
      };
    case 'non-compliant':
    case 'failed':
    case 'error':
    case 'unhealthy':
      return {
        bg: 'bg-red-500/10',
        text: 'text-red-600 dark:text-red-500',
        border: 'border-red-500/20',
      };
    case 'pending':
    case 'partial':
    case 'warning':
      return {
        bg: 'bg-yellow-500/10',
        text: 'text-yellow-600 dark:text-yellow-500',
        border: 'border-yellow-500/20',
      };
    case 'draft':
    case 'manual':
    case 'n/a':
      return {
        bg: 'bg-stone-100 dark:bg-nuguard-edge',
        text: 'text-stone-500 dark:text-slate-400',
        border: 'border-stone-200 dark:border-nuguard-edge',
      };
    default:
      return {
        bg: 'bg-stone-200 dark:bg-nuguard-card',
        text: 'text-stone-600 dark:text-slate-400',
        border: 'border-stone-200 dark:border-nuguard-edge',
      };
  }
}

// =============================================================================
// Validation Utilities
// =============================================================================

/**
 * Check if a string is a valid URL.
 */
export function isValidUrl(str: string): boolean {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if a string is a valid GitHub repository URL.
 */
export function isValidGitHubUrl(str: string): boolean {
  try {
    const url = new URL(str);
    return url.hostname === 'github.com' && url.pathname.split('/').filter(Boolean).length >= 2;
  } catch {
    return false;
  }
}

// =============================================================================
// ID Generation
// =============================================================================

/**
 * Generate a unique ID using crypto.randomUUID.
 */
export function generateId(): string {
  return crypto.randomUUID();
}

// =============================================================================
// Async Utilities
// =============================================================================

/**
 * Delay execution for a specified number of milliseconds.
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff.
 */
export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  baseDelayMs: number = 1000
): Promise<T> {
  let lastError: Error | undefined;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      if (attempt < maxAttempts) {
        await delay(baseDelayMs * Math.pow(2, attempt - 1));
      }
    }
  }
  
  throw lastError;
}
